import { useState, useEffect } from "react";
import { Card, CardHeader, CardBody, Col, Row, Button, Alert, Spinner } from "reactstrap";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import { MasterSheetTable } from "./MasterSheetTable";
import { PropertySelector } from "./PropertySelector";
import axios from "../../../api/axios";
import { ENDPOINTS, getMastersheet, getArchiveMastersheet, addTenant, updateTenant } from "../TenantForm/Helpers/api";
import { PreviewTenantForm } from "./PreviewTenantForm/PreviewTenantForm";

const ArchivedMastersheet = () => {
    const parsedAuthUser = JSON.parse(window.localStorage.getItem("authUser") || '{}');
    const userData = parsedAuthUser.userData || parsedAuthUser;
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [masterSheetData, setMasterSheetData] = useState({ items: [], mastersheetId: null });
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [currentProperty, setCurrentProperty] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [filterData, setFilterData] = useState({
        property_name: "",
        property_id: "",
        building_name: "",
    });
    // Everything loads in one go like the live mastersheet; a page holds 100 units, so
    // these only come into play once a building has archived more than 100 tenancies.
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState(null);

    const isValidSelection = !!filterData.property_id && (filterData.building_list?.length === 0 || !!filterData.building_name);

    const fetchMasterSheet = async (targetPage = 1) => {
        const { property_id, building_name } = filterData;
        if (!property_id) return;

        setIsLoading(true);
        try {
            // Fetch main MasterSheet to get mastersheetId
            const { data: mainData } = await getMastersheet({
                property_id,
                building_name,
            });

            // Fetch archived MasterSheet using mastersheetId
            let archivedData = { items: [], pagination: null };
            if (mainData?._id) {
                const { data } = await getArchiveMastersheet({ mastersheetId: mainData._id, page: targetPage });
                archivedData = data;
            }

            // Sort archived items by unitNo (numeric and alphanumeric)
            const sortedItems = (archivedData.items || []).sort((a, b) => {
                const unitNoA = parseInt(a.unitNo, 10);
                const unitNoB = parseInt(b.unitNo, 10);

                if (!isNaN(unitNoA) && !isNaN(unitNoB)) {
                    return unitNoA - unitNoB;
                }

                return String(a.unitNo ?? "").localeCompare(String(b.unitNo ?? ""), undefined, { numeric: 'base', sensitivity: 'base' });
            });

            setMasterSheetData({
                ...mainData,
                items: sortedItems,
                mastersheetId: mainData._id,
            });
            setPagination(archivedData.pagination || null);
            setPage(targetPage);
        } catch (error) {
            const message = error?.response?.data?.message || "Failed to fetch Archived Master Sheet.";
            if (message === "Building name is required for this property") {
                return;
            }
            if (message === "Master sheet not found for the given property") {
                setMasterSheetData({ items: [], mastersheetId: null });
            }
            Swal.fire("Error", message, "error");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isValidSelection) return;
        // A new property/building always starts back at the first page.
        fetchMasterSheet(1);
    }, [filterData.property_id, filterData.building_name]);

    const handleMasterSheetAction = async () => {
        try {
            const result = await Swal.fire({
                title: "Choose an Action",
                text: `Do you want to import a file to the ${filterData.property_name} Master Sheet or download the Archived Master Sheet?`,
                icon: "question",
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: "Download Archived Master Sheet",
                denyButtonText: "Import File",
                cancelButtonText: "Cancel",
            });

            if (result.isConfirmed) {
                const downloadConfirm = await Swal.fire({
                    title: "Are you sure?",
                    text: "Do you want to download the Archived Master Sheet Excel file?",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Yes, download!",
                    cancelButtonText: "No, cancel",
                });

                if (downloadConfirm.isConfirmed) {
                    const response = await axios.post(
                        ENDPOINTS.DOWNLOAD_MASTERSHEET_ITEMS,
                        { ...filterData, mastersheet_id: masterSheetData.mastersheetId, archived: true },
                        { responseType: "blob" }
                    );

                    const file = new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    saveAs(file, `ArchivedMasterSheet.xlsx`);
                    Swal.fire("Success!", "The Excel file has been downloaded.", "success");
                }
            } else if (result.isDenied) {
                const fileInput = document.createElement("input");
                fileInput.type = "file";
                fileInput.accept = ".xlsx, .xls";
                fileInput.style.display = "none";

                fileInput.addEventListener("change", async (event) => {
                    const file = event.target.files[0];
                    if (file) {
                        const formData = new FormData();
                        formData.append("file", file);
                        formData.append("property_id", filterData.property_id || "");
                        formData.append("building_name", filterData.building_name || "");
                        if (masterSheetData.mastersheetId) {
                            formData.append("mastersheet_id", masterSheetData.mastersheetId);
                        }

                        try {
                            setIsLoading(true);
                            const response = await axios.post(ENDPOINTS.UPLOAD_MASTERSHEET_ITEMS, formData, {
                                headers: { "Content-Type": "multipart/form-data" },
                            });

                            if (response.data.success) {
                                Swal.fire("Success!", "The file has been imported successfully.", "success");
                                fetchMasterSheet();
                            } else {
                                const errorMessages = response.data.errors.join("<br>");
                                Swal.fire("Validation Errors", errorMessages, "error");
                            }
                        } catch (error) {
                            if (error.response && error.response.status === 400) {
                                const errorData = error.response.data;
                                if (errorData.errors && Array.isArray(errorData.errors)) {
                                    const errorMessages = errorData.errors.join("<br>");
                                    Swal.fire("Validation Errors", errorMessages, "error");
                                } else {
                                    Swal.fire("Import Error", errorData.message || "Invalid data in the uploaded file.", "error");
                                }
                            } else {
                                Swal.fire("Error", "An error occurred while importing the file.", "error");
                            }
                        } finally {
                            setIsLoading(false);
                        }
                    }
                });
                document.body.appendChild(fileInput);
                fileInput.click();
                document.body.removeChild(fileInput);
            }
        } catch (error) {
            Swal.fire("Error", "An unexpected error occurred.", "error");
        }
    };

    const handleExportMastersheetAction = async () => {
        try {
            const confirmExport = await Swal.fire({
                title: "Download Archived Master Sheet",
                text: `Do you want to download the Archived Master Sheet for ${filterData.property_name}?`,
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Yes, download it!",
                cancelButtonText: "Cancel",
            });

            if (confirmExport.isConfirmed) {
                const response = await axios.post(
                    ENDPOINTS.DOWNLOAD_MASTERSHEET_ITEMS,
                    { ...filterData, mastersheet_id: masterSheetData.mastersheetId, archived: true },
                    { responseType: "blob" }
                );

                const file = new Blob([response.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                saveAs(file, `ArchivedMasterSheet.xlsx`);
                Swal.fire("Success!", "The Excel file has been downloaded.", "success");
            }
        } catch (error) {
            Swal.fire("Error", "An error occurred while exporting the Archived Master Sheet.", "error");
        }
    };

    const handleAddTenant = () => {
        setCurrentProperty(null);
        setEditMode(false);
        setShowForm(true);
    };

    const handleEditTenant = (tenantData) => {
        setCurrentProperty(tenantData);
        setEditMode(true);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setCurrentProperty(null);
        setEditMode(false);
    };

    const handleFormSubmit = async (formData) => {
        try {
            setIsLoading(true);
            const payload = {
                values: formData,
                property_id: filterData.property_id,
                building_name: filterData.building_name,
                mastersheet_id: masterSheetData.mastersheetId,
            };

            const response = editMode
                ? await updateTenant({ ...payload, _id: currentProperty._id })
                : await addTenant(payload);

            if (response?.status === 200 || response?.status === 201) {
                Swal.fire(
                    "Success",
                    `Tenant ${editMode ? "updated" : "added"} successfully`,
                    "success"
                );
                handleCloseForm();
                fetchMasterSheet(page);
            } else {
                const msg = response?.data?.message || `Failed to ${editMode ? "update" : "add"} tenant.`;
                Swal.fire("Error", msg, "error");
            }
        } catch (error) {
            if (error.response?.data?.errors?.length) {
                const messages = error.response.data.errors
                    .map((err) => `• ${err.message}`)
                    .join("\n");

                Swal.fire({
                    icon: "error",
                    title: "Validation Errors",
                    html: `<pre style="text-align: left;">${messages}</pre>`,
                });
            } else {
                const message =
                    error.response?.data?.message ||
                    error.message ||
                    `An unexpected error occurred while trying to ${editMode ? "update" : "add"} the tenant.`;
                Swal.fire("Error", message, "error");
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                <PreviewTenantForm
                    show={showForm}
                    handleClose={handleCloseForm}
                    propertyId={filterData.property_id || ""}
                    propertyName={filterData.property_name}
                    buildingName={filterData.building_name}
                    previewData={currentProperty}
                    tenantInfo={currentProperty}
                />
                <Row>
                    <Col xl={12}>
                        <Card>
                            <CardHeader>
                                <PropertySelector
                                    isFilterOpen={isFilterOpen}
                                    setIsFilterOpen={setIsFilterOpen}
                                    action={handleMasterSheetAction}
                                    export={handleExportMastersheetAction}
                                    userData={userData}
                                    filterData={filterData}
                                    setFilterData={setFilterData}
                                    isMastersheetAvailable={{ isValidSelection, masterSheetData }}
                                    onAddTenant={handleAddTenant}
                                />
                            </CardHeader>

                            {isLoading ? (
                                <CardBody className="text-center py-5">
                                    <Spinner color="primary" size="lg" className="mb-3" />
                                    <h5 className="text-muted">Loading...</h5>
                                </CardBody>
                            ) : (
                                <>
                                    {!isValidSelection && (
                                        <Alert color="danger" className="mt-3 text-center">
                                            <strong>⚠️ Please select both a Property and its corresponding Building to proceed.</strong>
                                        </Alert>
                                    )}

                                    {isValidSelection && masterSheetData.items.length <= 0 && (
                                        <Alert color="warning" className="mt-3 text-center">
                                            <strong>⚠️ No archived items are currently available for this Master Sheet.</strong>
                                            <p className="mb-0">
                                                Please ensure archived data exists for the selected property and building.
                                            </p>
                                        </Alert>
                                    )}

                                    {isValidSelection && masterSheetData === undefined && (
                                        <>
                                            {!isCreatingNew ? (
                                                <Alert color="warning" className="d-flex align-items-center justify-content-between p-3 border border-warning rounded">
                                                    <div className="d-flex align-items-center">
                                                        <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                                                        <strong>⚠️ No Layout Available for this Property</strong>
                                                    </div>
                                                    <Button
                                                        color={isCreatingNew ? "secondary" : "primary"}
                                                        className="fw-bold px-4"
                                                        onClick={() => setIsCreatingNew(!isCreatingNew)}
                                                    >
                                                        <i className="bi bi-plus-lg me-1"></i> Create New Layout
                                                    </Button>
                                                </Alert>
                                            ) : (
                                                <MasterSheetTable config={masterSheetData || { items: [] }} />
                                            )}
                                        </>
                                    )}

                                    {isValidSelection && masterSheetData?.items?.length > 0 && (
                                        <MasterSheetTable
                                            mastersheet={masterSheetData}
                                            onEditTenant={handleEditTenant}
                                            propertyId={filterData.property_id}
                                            buildingName={filterData.building_name}
                                            onDataUpdate={() => fetchMasterSheet(page)}
                                            showPreviousVersions
                                        />
                                    )}

                                    {/* Pages exist only past 100 archived units; below that the
                                        whole archive is already on screen and this never shows. */}
                                    {isValidSelection && pagination?.totalPages > 1 && (
                                        <div className="d-flex align-items-center justify-content-center gap-2 py-3">
                                            <Button
                                                size="sm"
                                                color="light"
                                                disabled={page <= 1 || isLoading}
                                                onClick={() => fetchMasterSheet(page - 1)}
                                            >
                                                ← Previous
                                            </Button>
                                            <span className="text-muted small">
                                                Page {pagination.page} of {pagination.totalPages} · {pagination.total} archived units
                                            </span>
                                            <Button
                                                size="sm"
                                                color="light"
                                                disabled={!pagination.hasNext || isLoading}
                                                onClick={() => fetchMasterSheet(page + 1)}
                                            >
                                                Next →
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export { ArchivedMastersheet };